import React from 'react';

/**
 * Flock-themed Icons Collection
 * Four beautiful icons for your BaadTV app
 */

// Helper function to convert hex color to CSS filter for stroke-based SVGs
const getColorFilter = (hexColor) => {
  if (hexColor === 'currentColor' || hexColor === '#000000') {
    return 'none';
  }
  
  // Yellow (#E62F88)
  if (hexColor === '#E62F88') {
    return 'invert(79%) sepia(77%) saturate(444%) hue-rotate(359deg) brightness(105%) contrast(94%)';
  }
  
  // Gray (#b0b0b0)
  if (hexColor === '#b0b0b0') {
    return 'invert(73%) sepia(0%) saturate(0%) hue-rotate(154deg) brightness(95%) contrast(88%)';
  }
  
  return 'none';
};

// Ribbon Icon - Perfect for awards, achievements, My BaadTV
export const RibbonIcon = ({ size = 24, color = 'currentColor', className = '' }) => {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      <g stroke={color} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" fill="none">
        <path d="m16 7 1.443 3.439 3.557.38-2.665 2.507.755 3.674-3.09-1.891-3.09 1.891.755-3.675-2.665-2.506 3.557-.38z"/>
        <path d="m16 21c4.957 0 9-4.043 9-9s-4.043-9-9-9-9 4.043-9 9 4.043 9 9 9z"/>
        <path d="m21 19.484v9.516l-5-2.999-5 2.999v-9.516"/>
      </g>
    </svg>
  );
};

// Hay Roll Icon - Perfect for feed, content consumption (round bale)
export const HayRollIcon = ({ size = 24, color = 'currentColor', className = '' }) => {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 512 512"
      width={size}
      height={size}
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      <path 
        fill={color}
        d="m474.399 102.343c-26.212-44.717-62.224-69.343-101.399-69.343h-234c-39.175 0-75.187 24.626-101.399 69.343-24.247 41.363-37.601 95.932-37.601 153.657s13.354 112.295 37.601 153.657c26.212 44.717 62.224 69.343 101.399 69.343h234c31.803 0 62.559-16.51 86.602-46.486 6.911-8.617 5.528-21.205-3.089-28.115-8.616-6.911-21.204-5.527-28.115 3.088-12.087 15.072-26.032 25.115-40.354 29.302 6.327-9.335 11.72-19.609 16.124-29.248 15.959-34.929 26.814-79.079 29.782-121.132 4.802-68.031-5.736-121.313-28.913-146.183-11.726-12.584-24.166-15.226-32.537-15.226-15.544 0-30.926 11.034-42.201 30.271-7.435 12.688-16.299 34.506-16.299 66.396 0 12.406 1.78 24.462 5.013 33.95 7.395 21.699 20.708 24.939 27.987 24.939 11.046 0 20-8.954 20-20 0-7.381-3.999-13.828-9.947-17.293-1.341-3.727-3.053-11.32-3.053-21.596 0-34.726 13.776-52.681 19.232-56.339.565.333 1.427.971 2.542 2.168 12.248 13.143 22.649 54.138 18.275 116.096-2.475 35.063-11.211 72.286-23.967 102.127-12.374 28.945-24.425 41.389-28.773 43.261-38.682-25.062-67.309-94.158-67.309-172.98 0-99.196 45.336-183 99-183s99 83.804 99 183c0 28.131-4.107 56.534-12.207 84.422-3.081 10.607 3.021 21.704 13.628 24.784 10.604 3.079 21.704-3.021 24.785-13.628 9.153-31.515 13.794-63.671 13.794-95.578 0-57.725-13.354-112.294-37.601-153.657zm-202.798 0c-24.247 41.363-37.601 95.932-37.601 153.657s13.354 112.295 37.601 153.657c6.464 11.027 13.527 20.827 21.083 29.343h-52.684c-25.656 0-54.79-19.128-77.933-51.168-26.099-36.132-41.067-84.183-41.067-131.832 0-11.046-8.954-20-20-20s-20 8.954-20 20c0 56.691 17.274 111.828 48.642 155.254 7.47 10.342 15.47 19.611 23.85 27.746h-14.492c-53.664 0-99-83.804-99-183s45.336-183 99-183h66.794c-7.191 8.749-13.791 18.856-19.698 30.256-20.697 39.945-32.096 94.191-32.096 152.744 0 11.046 8.954 20 20 20s20-8.954 20-20c0-52.267 9.806-99.977 27.612-134.341 11.502-22.198 31.489-48.659 61.388-48.659h9.684c-7.557 8.516-14.619 18.315-21.083 29.343z"
      />
    </svg>
  );
};

// Hay Bale Icon - Perfect for feed, content consumption (keeping for backwards compatibility)
export const HayBaleIcon = ({ size = 24, color = 'currentColor', className = '' }) => {
  return (
    <img
      src="/icons-hay-bale.svg"
      width={size}
      height={size}
      alt="Hay Bale"
      className={className}
      style={{ 
        display: 'inline-block', 
        verticalAlign: 'middle',
        filter: getColorFilter(color)
      }}
    />
  );
};

// Fence Icon - Perfect for dashboard/home, entering the pasture
export const FenceIcon = ({ size = 24, color = 'currentColor', className = '' }) => {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 510 510"
      width={size}
      height={size}
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      <path 
        fill={color}
        d="m495 40h-64c-8.284 0-15 6.716-15 15v41h-322v-41c0-8.284-6.716-15-15-15h-64c-8.284 0-15 6.716-15 15v400c0 8.284 6.716 15 15 15h64c8.284 0 15-6.716 15-15v-41h322v41c0 8.284 6.716 15 15 15h64c8.284 0 15-6.716 15-15v-400c0-8.284-6.716-15-15-15zm-465 130.174v-37.517c341.331 159.183 100.278 46.77 450 209.839v37.518c-463.981-216.373-152.193-70.976-450-209.84zm0 210.039v-37.516l149.495-69.718 40.231 18.761c-175.961 82.049-132.272 61.676-189.726 88.473zm113.998-123.782-49.998 23.317v-46.63zm-79.998 37.307-34 15.856v-106.319l34 15.854zm416-160.884v37.52l-149.071 69.506-40.222-18.752c185.806-86.64 187.61-87.489 189.293-88.274zm-113.577 123.577 49.577-23.116v46.234zm79.577-37.104 34-15.853v105.92l-34-15.855zm0-149.327h34v29.753l-34 15.853zm-30 56v3.604l-160.794 74.978-161.206-75.181v-3.401zm-352-56v45.41l-34-15.856v-29.554zm0 370h-34v-26.685l34-15.858zm30-56c0-.83-7.88 3.141 161.218-75.708 172.997 80.675 160.782 74.573 160.782 75.708zm352 56v-42.739l34 15.855v26.884z"
      />
    </svg>
  );
};

// Gate Icon - Perfect for dashboard/home, entering the flock (keeping for backwards compatibility)
export const GateIcon = ({ size = 24, color = 'currentColor', className = '' }) => {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      <path 
        fill={color}
        d="m86 10h-5c-2.2055664 0-4 1.7939453-4 4v3.6650391h-54v-3.6650391c0-2.2060547-1.7944336-4-4-4h-5c-2.2055664 0-4 1.7939453-4 4v75c0 .5527344.4477539 1 1 1h11c.5522461 0 1-.4472656 1-1v-6.6650391h54v6.6650391c0 .5527344.4477539 1 1 1h11c.5522461 0 1-.4472656 1-1v-75c0-2.2060547-1.7944336-4-4-4zm-63 31.5571289 11.2431641 7.1366577v19.9800415h-11.2431641zm13.2431641 8.406189 12.7741089 8.1084595c-.0026246.029602-.017273.0551147-.017273.0854492v10.5166016h-12.7568359zm14.7568359 9.3670654 14.7196655 9.3434448h-14.7196655zm18.4534912 9.3434448-46.4534912-29.4868164v-7.9614258h6.8335571l47.1664429 29.9289551v7.5192871zm-5.6966553-37.4482422v19.1564331l-12.7568359-8.0947265v-11.0617066zm13.2431641 0v27.5597534l-11.2431641-7.1342163v-20.4255371zm-28 9.7926636-15.4326782-9.7926636h15.4326782zm28-21.3532104v9.5605469h-54v-9.5605469zm-56 68.3349609h-9v-74c0-1.1025391.8969727-2 2-2h5c1.1030273 0 2 .8974609 2 2zm2-7.6650391v-9.6611328h54v9.6611328zm65 7.6650391h-9v-74c0-1.1025391.8969727-2 2-2h5c1.1030273 0 2 .8974609 2 2z"
      />
    </svg>
  );
};

// Sheep Icon - Perfect for user profiles, navigation, general sheep theme
export const SheepIcon = ({ size = 24, color = 'currentColor', className = '' }) => {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      <path 
        fill={color}
        d="m24,1.771s-1.667-.513-3.748-.829c-.761-.59-1.716-.942-2.752-.942-1.005,0-1.935.331-2.685.891-2.125.324-3.907.814-3.907.814.271,1.102,1.111,1.915,2.13,2.205-.025.193-.039.391-.039.591v1.537c-.536.079-1.05.285-1.5.602-.589-.415-1.285-.64-2-.64-1.05,0-2.025.466-2.687,1.267-.423-.177-.863-.267-1.313-.267-1.93,0-3.5,1.57-3.5,3.5,0,.113.006.227.019.343-1.223.57-2.019,1.79-2.019,3.157s.795,2.586,2.019,3.157c-.012.116-.019.229-.019.343,0,1.76,1.306,3.221,3,3.464v3.036h3v-2.339c.461.22.971.339,1.5.339.715,0,1.411-.225,2-.64.589.415,1.285.64,2,.64.955,0,1.849-.386,2.5-1.058v3.058h3v-3.338c1.181-.563,2-1.769,2-3.162,0-.113-.006-.227-.019-.343,1.223-.571,2.019-1.791,2.019-3.157,0-.648-.184-1.26-.502-1.788-.236.236-.494.453-.779.645.18.345.281.735.281,1.143,0,1.077-.696,2.028-1.733,2.365l-.433.141s.166.822.166.994c0,1.379-1.122,2.5-2.5,2.5-.421,0-.832-.112-1.223-.335l-.415-.236-.255.403c-.463.731-1.251,1.168-2.107,1.168-.608,0-1.2-.23-1.666-.65l-.334-.301-.334.301c-.466.42-1.057.65-1.666.65-.856,0-1.645-.437-2.107-1.168l-.255-.403-.415.236c-.391.223-.802.335-1.223.335-1.378,0-2.5-1.121-2.5-2.5,0-.172.166-.994.166-.994l-.433-.141c-1.037-.337-1.733-1.288-1.733-2.365s.696-2.028,1.732-2.365l.434-.141s-.166-.822-.166-.995c0-1.378,1.122-2.5,2.5-2.5.42,0,.832.113,1.224.335l.414.234.255-.402c.463-.731,1.251-1.167,2.107-1.167.608,0,1.2.231,1.666.65l.334.301.334-.301c.337-.303.739-.507,1.166-.597v1.326c0,2.268,1.682,3.621,4.5,3.621s4.5-1.354,4.5-3.621v-3.879c0-.2-.013-.398-.039-.591.977-.291,1.778-1.077,2.039-2.138Zm-7,19.192c.165.025.332.037.5.037.17,0,.337-.012.5-.036v2.036h-1v-2.037Zm-11,0c.277-.042.549-.118.813-.229.077.094.159.182.245.267h-.058v2h-1v-2.037Zm15-12.584c0,2.281-2.193,2.621-3.5,2.621s-3.5-.34-3.5-2.621v-3.879c0-1.93,1.57-3.5,3.5-3.5s3.5,1.57,3.5,3.5v3.879Zm-1-3.379c0,.552-.448,1-1,1s-1-.448-1-1,.448-1,1-1,1,.448,1,1Zm-3,0c0,.552-.448,1-1,1s-1-.448-1-1,.448-1,1-1,1,.448,1,1Z"
      />
    </svg>
  );
};

// Badge Icon - Perfect for achievements, awards, featured content
export const BadgeIcon = ({ size = 24, color = 'currentColor', className = '' }) => {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 74 74"
      width={size}
      height={size}
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      <path 
        fill={color}
        d="M30.64,56.956a3.775,3.775,0,0,1-.968-.124c-1.578-.422-2.618-1.842-3.535-3.095a7.662,7.662,0,0,0-1.929-2.1,7.759,7.759,0,0,0-2.834-.642c-1.6-.179-3.261-.363-4.394-1.5s-1.318-2.791-1.5-4.394a7.782,7.782,0,0,0-.64-2.834,7.7,7.7,0,0,0-2.1-1.929c-1.254-.917-2.674-1.958-3.095-3.535-.405-1.515.255-3.025.895-4.485a8.039,8.039,0,0,0,.878-2.844,8.039,8.039,0,0,0-.878-2.844c-.64-1.46-1.3-2.969-.895-4.484.421-1.578,1.841-2.618,3.094-3.535a7.7,7.7,0,0,0,2.1-1.929,7.786,7.786,0,0,0,.641-2.834c.179-1.6.364-3.26,1.5-4.393s2.792-1.318,4.394-1.5a7.759,7.759,0,0,0,2.834-.642,7.668,7.668,0,0,0,1.93-2.1c.917-1.253,1.957-2.673,3.534-3.094,1.514-.406,3.025.255,4.485.895A8.039,8.039,0,0,0,37,3.9a8.039,8.039,0,0,0,2.844-.878c1.46-.639,2.973-1.3,4.484-.895,1.578.421,2.618,1.842,3.535,3.1a7.691,7.691,0,0,0,1.929,2.1,7.76,7.76,0,0,0,2.834.641c1.6.179,3.261.364,4.394,1.5s1.318,2.791,1.5,4.394a7.792,7.792,0,0,0,.64,2.834,7.71,7.71,0,0,0,2.1,1.928c1.254.918,2.674,1.958,3.095,3.536.405,1.515-.255,3.025-.895,4.485a8.029,8.029,0,0,0-.878,2.843,8.039,8.039,0,0,0,.878,2.844c.64,1.46,1.3,2.969.895,4.484-.421,1.578-1.841,2.618-3.094,3.536a7.68,7.68,0,0,0-2.1,1.929,7.781,7.781,0,0,0-.641,2.834c-.179,1.6-.364,3.26-1.5,4.393s-2.792,1.318-4.394,1.5a7.76,7.76,0,0,0-2.834.641,7.668,7.668,0,0,0-1.93,2.1c-.917,1.253-1.957,2.673-3.534,3.094-1.516.4-3.025-.255-4.485-.894A8.039,8.039,0,0,0,37,55.059a8.039,8.039,0,0,0-2.844.878A9.144,9.144,0,0,1,30.64,56.956Zm6.36-3.9A9.381,9.381,0,0,1,40.646,54.1c1.2.524,2.325,1.019,3.166.8.9-.24,1.645-1.262,2.438-2.344A9.087,9.087,0,0,1,48.791,49.9a9.223,9.223,0,0,1,3.614-.9c1.319-.147,2.564-.286,3.2-.923s.777-1.883.923-3.2a9.2,9.2,0,0,1,.9-3.614,9.1,9.1,0,0,1,2.65-2.541c1.082-.792,2.1-1.541,2.345-2.438.223-.838-.272-1.969-.8-3.166a9.382,9.382,0,0,1-1.046-3.646,9.356,9.356,0,0,1,1.046-3.645c.524-1.2,1.019-2.328.795-3.167-.239-.9-1.262-1.646-2.345-2.438a9.1,9.1,0,0,1-2.65-2.541,9.2,9.2,0,0,1-.9-3.613c-.147-1.319-.286-2.564-.924-3.2s-1.882-.776-3.2-.923a9.223,9.223,0,0,1-3.614-.9A9.1,9.1,0,0,1,46.25,6.4c-.793-1.082-1.541-2.1-2.438-2.344-.835-.223-1.969.271-3.166.8A9.377,9.377,0,0,1,37,5.9,9.377,9.377,0,0,1,33.354,4.85c-1.2-.524-2.329-1.019-3.166-.795-.9.239-1.645,1.262-2.438,2.344a9.1,9.1,0,0,1-2.541,2.652,9.223,9.223,0,0,1-3.614.9c-1.319.147-2.564.285-3.2.923s-.776,1.882-.923,3.2a9.207,9.207,0,0,1-.9,3.615,9.1,9.1,0,0,1-2.65,2.541c-1.083.792-2.105,1.541-2.345,2.437-.223.838.272,1.969.8,3.166a9.382,9.382,0,0,1,1.046,3.646,9.365,9.365,0,0,1-1.046,3.646c-.524,1.2-1.019,2.328-.795,3.167.239.9,1.262,1.645,2.345,2.438a9.105,9.105,0,0,1,2.65,2.54,9.205,9.205,0,0,1,.9,3.614c.147,1.319.286,2.564.924,3.2s1.882.776,3.2.923a9.24,9.24,0,0,1,3.614.9,9.109,9.109,0,0,1,2.54,2.651c.793,1.082,1.541,2.1,2.438,2.344.838.225,1.968-.271,3.166-.8A9.381,9.381,0,0,1,37,53.059Z"
      />
      <path 
        fill={color}
        d="M37,48.674a19.2,19.2,0,1,1,19.2-19.2A19.219,19.219,0,0,1,37,48.674ZM37,12.28a17.2,17.2,0,1,0,17.2,17.2A17.217,17.217,0,0,0,37,12.28Z"
      />
      <path 
        fill={color}
        d="M42.774,38.274a1,1,0,0,1-.465-.115L37,35.368l-5.309,2.791A1,1,0,0,1,30.24,37.1l1.014-5.913-4.3-4.186a1,1,0,0,1,.555-1.706l5.935-.863L36.1,19.058a1.04,1.04,0,0,1,1.792,0l2.656,5.379,5.935.863a1,1,0,0,1,.555,1.706l-4.3,4.186L43.76,37.1a1,1,0,0,1-.986,1.169ZM37,33.238a1,1,0,0,1,.466.115l3.98,2.093-.759-4.434a1,1,0,0,1,.287-.885l3.22-3.139-4.45-.647a1,1,0,0,1-.753-.548L37,21.76l-1.991,4.033a1,1,0,0,1-.753.548l-4.45.647,3.22,3.139a1,1,0,0,1,.287.885l-.759,4.434,3.98-2.093A1,1,0,0,1,37,33.238Z"
      />
      <path 
        fill={color}
        d="M44.75,72c-.03,0-.061,0-.091,0a1,1,0,0,1-.846-.646L37.6,54.7a.994.994,0,0,1-.064-.349,1,1,0,0,1,1.965-.275L44.96,68.7l3.988-6.672a1,1,0,0,1,1.17-.437l7.4,2.426L52.369,50.249a1,1,0,0,1,1.873-.7l5.875,15.718a1,1,0,0,1-1.248,1.3l-8.614-2.825-4.647,7.772A1,1,0,0,1,44.75,72Z"
      />
      <path 
        fill={color}
        d="M29.25,72a1,1,0,0,1-.858-.487l-4.647-7.772-8.614,2.825a1,1,0,0,1-1.248-1.3l5.875-15.718a1,1,0,0,1,1.873.7L16.484,64.018l7.4-2.426a1,1,0,0,1,1.17.437L29.04,68.7,34.526,54a1,1,0,1,1,1.873.7L30.187,71.35a1,1,0,0,1-.846.646C29.311,72,29.28,72,29.25,72Z"
      />
    </svg>
  );
};

// You can also use the icons from files if you prefer
export const SheepIconFromFile = ({ size = 24, className = '' }) => {
  return (
    <img
      src="/icons-sheep.svg"
      width={size}
      height={size}
      alt="Sheep"
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    />
  );
};

export const BadgeIconFromFile = ({ size = 24, className = '' }) => {
  return (
    <img
      src="/icons-badge.svg"
      width={size}
      height={size}
      alt="Badge"
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    />
  );
};

export const GateIconFromFile = ({ size = 24, className = '' }) => {
  return (
    <img
      src="/icons-gate.svg"
      width={size}
      height={size}
      alt="Gate"
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    />
  );
};

export const HayBaleIconFromFile = ({ size = 24, className = '' }) => {
  return (
    <img
      src="/icons-hay-bale.svg"
      width={size}
      height={size}
      alt="Hay Bale"
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    />
  );
};

export const FenceIconFromFile = ({ size = 24, className = '' }) => {
  return (
    <img
      src="/icons-fence.svg"
      width={size}
      height={size}
      alt="Fence"
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    />
  );
};

export const RibbonIconFromFile = ({ size = 24, className = '' }) => {
  return (
    <img
      src="/icons-ribbon.svg"
      width={size}
      height={size}
      alt="Ribbon"
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    />
  );
};

export const HayRollIconFromFile = ({ size = 24, className = '' }) => {
  return (
    <img
      src="/icons-hay-roll.svg"
      width={size}
      height={size}
      alt="Hay Roll"
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    />
  );
};

export default { HayRollIcon, RibbonIcon, FenceIcon, HayBaleIcon, GateIcon, SheepIcon, BadgeIcon, HayRollIconFromFile, RibbonIconFromFile, FenceIconFromFile, HayBaleIconFromFile, GateIconFromFile, SheepIconFromFile, BadgeIconFromFile };
